import React from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { DS } from '../../ds';
import { ShareButtons } from './ShareButtons';
import { useDisqusThread } from '../../../hooks/useDisqusThread';
import type { BlogArticle } from '../../../data/blogArticles';
interface ArticleDetailProps {
  article: BlogArticle;
  onBack: () => void;
  onHome: () => void;
}
function NavButtons({
  onBack,
  onHome,
  justify = 'flex-start'
}: {
  onBack: () => void;
  onHome: () => void;
  justify?: React.CSSProperties['justifyContent'];
}) {
  return <div style={{
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    justifyContent: justify
  }} data-id="element-219" data-mp="9sqbdfhzp9fj">
      <button type="button" onClick={onBack} style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '7px',
      background: 'transparent',
      border: `1.5px solid ${DS.color.teal}`,
      color: DS.color.teal,
      padding: '9px 18px',
      borderRadius: DS.radius.pill,
      fontFamily: DS.font.body,
      fontSize: '13px',
      fontWeight: 700,
      cursor: 'pointer',
      transition: `background-color 0.2s ${DS.ease.smooth}`
    }} onMouseEnter={e => {
      e.currentTarget.style.background = DS.color.mint;
    }} onMouseLeave={e => {
      e.currentTarget.style.background = 'transparent';
    }} data-id="element-220" data-mp="jtafcpww8s1g">
        <ArrowLeftIcon size={15} aria-hidden="true" data-id="element-221" data-mp="k2cufrc48526" />
        Volver a artículos
      </button>

      <button type="button" onClick={onHome} style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '7px',
      background: 'transparent',
      border: `1.5px solid ${DS.color.border}`,
      color: DS.color.body,
      padding: '9px 18px',
      borderRadius: DS.radius.pill,
      fontFamily: DS.font.body,
      fontSize: '13px',
      fontWeight: 700,
      cursor: 'pointer',
      transition: `border-color 0.2s ${DS.ease.smooth}, color 0.2s ${DS.ease.smooth}`
    }} onMouseEnter={e => {
      e.currentTarget.style.borderColor = DS.color.teal;
      e.currentTarget.style.color = DS.color.teal;
    }} onMouseLeave={e => {
      e.currentTarget.style.borderColor = DS.color.border;
      e.currentTarget.style.color = DS.color.body;
    }} data-id="element-222" data-mp="f39p05s83wto">
        Volver a inicio
      </button>
    </div>;
}
export const ArticleDetail = ({
  article,
  onBack,
  onHome
}: ArticleDetailProps) => {
  const {
    loadError
  } = useDisqusThread({
    identifier: `article-${article.id}`,
    title: article.title
  });
  return <div data-id="element-223" data-mp="24n9mpo05gio7">
      <div style={{
      marginBottom: '32px'
    }} data-id="element-224" data-mp="24mzwq0f3ubii">
        <NavButtons onBack={onBack} onHome={onHome} data-id="element-225" data-mp="1qmyvukyub431" />
      </div>

      <article style={{
      maxWidth: '760px',
      margin: '0 auto'
    }} data-id="element-226" data-mp="1qotflvydkq5j">
        <div style={{
        width: '100%',
        aspectRatio: '16 / 9',
        borderRadius: DS.radius.cardLg,
        overflow: 'hidden',
        marginBottom: '32px',
        background: DS.color.linen
      }} data-id="element-227" data-mp="1un911vet5qgu">
          <img src={article.image} alt={article.title} style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }} data-id="element-228" data-mp="1l0g2fjxyiols" />
        </div>

        <div style={{
        display: 'flex',
        gap: '14px',
        alignItems: 'center',
        flexWrap: 'wrap',
        marginBottom: '18px'
      }} data-id="element-229" data-mp="1g3y16u1u58e7">
          <span style={{
          background: DS.color.mint,
          color: DS.color.tealDeep,
          padding: '4px 10px',
          borderRadius: '8px',
          fontFamily: DS.font.body,
          fontSize: '11px',
          fontWeight: 700
        }} data-id="element-230" data-mp="23r66hbz9khg8">
            {article.category}
          </span>
        </div>

        <h1 style={{
        fontFamily: DS.font.heading,
        fontSize: 'clamp(2rem, 4.5vw, 2.8rem)',
        fontWeight: 700,
        color: DS.color.ink,
        lineHeight: 1.18,
        letterSpacing: '-0.025em',
        margin: '0 0 20px'
      }} data-id="element-231" data-mp="28o66b55isnyw">
          {article.title}
        </h1>

        <div style={{
        display: 'flex',
        gap: '14px',
        alignItems: 'center',
        flexWrap: 'wrap',
        paddingBottom: '28px',
        borderBottom: `1px solid ${DS.color.border}`,
        marginBottom: '32px',
        fontFamily: DS.font.body,
        fontSize: '14px',
        color: DS.color.muted
      }} data-id="element-232" data-mp="36k1s019zj4ip">
          <span style={{
          color: DS.color.ink,
          fontWeight: 700
        }} data-id="element-233" data-mp="2nubtp470k0m1">
            Por {article.author}
          </span>
          <span aria-hidden="true" data-id="element-234" data-mp="3u8uopzyflged">·</span>
          <span data-id="element-235" data-mp="332yl0knm3rk">{article.date}</span>
          <span aria-hidden="true" data-id="element-236" data-mp="320b72mjijge">·</span>
          <span data-id="element-237" data-mp="355zifnchhb2">{article.readTime} min de lectura</span>
        </div>

        <p style={{
        fontFamily: DS.font.body,
        fontSize: '18px',
        color: DS.color.ink,
        lineHeight: 1.6,
        fontWeight: 600,
        margin: '0 0 8px'
      }} data-id="element-238" data-mp="1njsr3emygv0o">
          {article.excerpt}
        </p>

        <div className="article-body-full" style={{
        fontFamily: DS.font.body,
        fontSize: '16px',
        lineHeight: 1.8,
        color: DS.color.body
      }} dangerouslySetInnerHTML={{
        __html: article.fullContent
      }} data-id="element-239" data-mp="18v6bdbtf218x" />

        <div style={{
        marginTop: '48px',
        paddingTop: '32px',
        borderTop: `1px solid ${DS.color.border}`
      }} data-id="element-240" data-mp="uvn5yqrzp40b">
          <h3 style={{
          fontFamily: DS.font.heading,
          fontSize: '15px',
          fontWeight: 700,
          color: DS.color.ink,
          margin: '0 0 14px'
        }} data-id="element-241" data-mp="2pa7hodyc1p7k">
            Compartir artículo
          </h3>
          <ShareButtons article={article} data-id="element-242" data-mp="ike1pji9t2lj" />
        </div>

        <section style={{
        marginTop: '48px',
        padding: '32px',
        background: 'rgba(0, 170, 212, 0.08)',
        border: `1px solid ${DS.color.aqua}30`,
        borderRadius: DS.radius.cardLg,
        textAlign: 'center'
      }} data-id="element-243" data-mp="ndp85jj67hcx">
          <h3 style={{
          fontFamily: DS.font.heading,
          fontSize: '20px',
          fontWeight: 700,
          color: DS.color.ink,
          margin: '0 0 10px'
        }} data-id="element-244" data-mp="11dzxgi4u0nvz">
            ¿Listo para transformar tu práctica?
          </h3>
          <p style={{
          fontFamily: DS.font.body,
          fontSize: '15px',
          color: DS.color.body,
          margin: '0 0 22px'
        }} data-id="element-245" data-mp="wc0vncoqpz15">
            Descubre cómo Alinova puede llevar tu clínica al siguiente nivel.
          </p>
          <a href={DS.wa} target="_blank" rel="noopener noreferrer" style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '13px 28px',
          borderRadius: DS.radius.pill,
          background: '#00AAD4',
          boxShadow: '0 8px 26px rgba(0, 170, 212, 0.42)',
          color: '#fff',
          fontFamily: DS.font.body,
          fontSize: '14px',
          fontWeight: 700,
          textDecoration: 'none',
          transition: `background-color 0.22s ${DS.ease.smooth}, box-shadow 0.22s ${DS.ease.smooth}`
        }} onMouseEnter={e => {
          e.currentTarget.style.background = '#0099CC';
          e.currentTarget.style.boxShadow = '0 12px 34px rgba(0, 170, 212, 0.55)';
        }} onMouseLeave={e => {
          e.currentTarget.style.background = '#00AAD4';
          e.currentTarget.style.boxShadow = '0 8px 26px rgba(0, 170, 212, 0.42)';
        }} data-id="element-246" data-mp="39j0gb5f7dwf4">
            Solicitar consulta
            <ArrowRightIcon size={15} aria-hidden="true" data-id="element-247" data-mp="1rjck9w7y11jr" />
          </a>
        </section>

        <div style={{
        marginTop: '48px',
        paddingTop: '32px',
        borderTop: `1px solid ${DS.color.border}`
      }} data-id="element-248" data-mp="1vkpknqs9ocja">
          <h3 style={{
          fontFamily: DS.font.heading,
          fontSize: '15px',
          fontWeight: 700,
          color: DS.color.ink,
          margin: '0 0 14px'
        }} data-id="element-249" data-mp="20hof6afueli0">
            Comentarios
          </h3>

          {!loadError ? <div style={{
          background: '#fff',
          border: `1px solid ${DS.color.border}`,
          borderRadius: DS.radius.cardLg,
          padding: '24px',
          minHeight: '260px',
          boxSizing: 'border-box'
        }} data-id="element-250" data-mp="2fahw80arj9os">
              <div id="disqus_thread" data-id="element-251" data-mp="7n6q1catf57m" />
              <noscript data-id="element-252" data-mp="7m5fd8xpokqf">
                Por favor habilita JavaScript para ver los comentarios.
              </noscript>
            </div> : <div style={{
          background: '#fff',
          border: `1px dashed ${DS.color.border}`,
          borderRadius: DS.radius.cardLg,
          padding: '32px 24px',
          textAlign: 'center'
        }} data-id="element-253" data-mp="3urwbvgftjfll">
              <p style={{
            fontFamily: DS.font.body,
            fontSize: '14px',
            color: DS.color.body,
            margin: 0
          }} data-id="element-254" data-mp="rda5v8f0lhew">
                No fue posible cargar los comentarios de este artículo.
              </p>
            </div>}
        </div>

        <div style={{
        marginTop: '32px',
        paddingTop: '32px',
        borderTop: `1px solid ${DS.color.border}`
      }} data-id="element-255" data-mp="1a909t16icnvf">
          <NavButtons onBack={onBack} onHome={onHome} justify="center" data-id="element-256" data-mp="14qxx2tvlocb2" />
        </div>
      </article>

      <style data-id="element-257" data-mp="1iq5wnnvodmfs">
        {`
          .article-body-full h3 {
            font-family: ${DS.font.heading};
            font-size: 18px;
            font-weight: 700;
            color: ${DS.color.tealDeep};
            margin: 28px 0 10px;
          }
          .article-body-full p {
            margin: 0 0 16px;
          }
          .article-body-full ul {
            margin: 0 0 16px;
            padding-left: 20px;
          }
          .article-body-full li {
            margin-bottom: 8px;
          }
        `}
      </style>
    </div>;
};